<template>
  <div
    class="page-search-hotels"
    :class="{
      [`is-type-${searchType.id}`]: true,
    }"
  >
    <aside
      class="page-search-hotels__sidebar"
    >
      <filters-sidebar
        :filters="filters"
        class="filters"
      />
    </aside>
    <div class="page-search-hotels__search">
      <search-panel
        :search-type="searchType"
        :search-item="$route.query.city"
        @submit="submit"
        @city="city = $event"
        @clear="clearSearchFilters"
      />
      <div style="padding: 30px; border-top: 1px solid rgba(110, 124, 145, 0.07); display: flex; align-items: center;">
        <filter-tags
          :features="selectedFeatures.filter(feature => searchType !== 'property' || feature !== 'Deluxe')"
          :location="city ? city.label : null"
          :amenities="selectedFacilities"
          :poi="['Ocean view']"
          :rating="['4-5 stars', '4-5 on trustyou']"
        />
        <filter-btn style="margin-left: auto;" />
        <div class="vertical-separator" />
        <menu-btn />
      </div>
    </div>
    <div
      v-if="!isLoading && items"
      class="page-search-hotels__content"
    >
      <component
        :is="card"
        v-for="item in items"
        :key="item.id"
        :selected-facilities="selectedFacilities"
        v-bind="item"
      />
    </div>
  </div>
</template>

<script>
import { mapActions, mapState } from 'vuex';
import { cloneDeep, omit } from 'lodash';
import * as filters from '@/helpers/filters';
import FiltersSidebar from '@/components/search/FiltersSidebar';
import SearchPanel from '@/components/search/SearchPanel';
import FilterTags from '@/components/FilterTags';
import FilterBtn from '../components/tiny/FilterBtn';
import MenuBtn from '../components/tiny/MenuBtn';
import { searchTypeOptions, transformHotelData } from '@/helpers';
import HotelCard from '@/components/SearchResultsCard';
import HotelRoomsCard from '@/components/SearchResultsCardRoom';

export default {
  name: 'page-search-hotels',
  components: {
    FiltersSidebar,
    HotelCard,
    HotelRoomsCard,
    FilterTags,
    FilterBtn,
    MenuBtn,
    SearchPanel,
  },
  data() {
    let searchType = searchTypeOptions[0].id;
    if (this.$route.query && this.$route.query.type) {
      const option = searchTypeOptions.find(({ id }) => id === this.$route.query.type);
      searchType = (option && option.id) || searchTypeOptions[0].id;
    }
    return {
      isLoading: false,
      city: null,
      searchType,
      searchTypeOptions,
      filters: {
        starRating: cloneDeep(filters.starRating),
        priceRange: cloneDeep(filters.priceRange),
        propertyType: cloneDeep(filters.propertyType),
        amenities: cloneDeep(filters.amenities),
        roomClass: cloneDeep(filters.roomClass),
        pointOfInterest: cloneDeep(filters.pointOfInterest),
        bedding: cloneDeep(filters.bedding),
        occupancy: cloneDeep(filters.occupancy),
        roomSize: cloneDeep(filters.roomSize),
        mealPlan: cloneDeep(filters.mealPlan),
      },
      selectedFacilities: ['Fitness center', 'Outdoor Pool', 'WiFi'],
      selectedFeatures: ['Refundable', 'Hotel', 'Deluxe', '0-350sqm', '$99-$599'],
    };
  },
  computed: {
    ...mapState([
      'hotelsAll',
      'roomsAll',
    ]),
    card() {
      switch (this.searchType) {
        case 'property':
          return 'hotel-card';
        case 'room':
          return 'hotel-rooms-card';
        default:
          return 'hotel-card';
      }
    },
    hotelsTransformed() {
      return this.hotelsAll
        .map(hotel => transformHotelData(hotel))
        .filter(({ raw }) => this.city ? raw.cityName.toLowerCase() === this.city.id : true);
    },
    items() {
      switch (this.searchType) {
        case 'property':
          return this.hotelsTransformed;
        case 'room':
          return this.hotelsTransformed
            .map(hotel => ({
              ...hotel,
              roomsList: this.transformToRoomsList(this.roomsAll[hotel.id]),
            }))
            .filter(hotel => hotel.roomsList.length > 0);
        default:
          return this.hotelsTransformed;
      }
    },
  },
  watch: {
    '$route.fullPath': {
      handler() {
        if (this.$route.query && this.$route.query.type) {
          const searchTypeOption = searchTypeOptions.find(({ id }) => id === this.$route.query.type);
          if (searchTypeOption) {
            this.searchType = searchTypeOption.id;
          }
        } else {
          this.searchType = searchTypeOptions[0].id;
        }
      },
    },
    searchType: {
      async handler(val) {
        if (this.$route.query && this.$route.query.type === val) {
          return;
        }
        this.$router.replace({
          name: 'search-results',
          query: {
            type: val.id,
          },
        });
      },
    },
  },
  async created() {
    const loader = this.$loading.show();
    this.isLoading = true;

    await Promise.all([
      this.getHotelFacilitiesList(),
      this.getAllHotels(),
      this.getAllRooms(),
    ]);

    this.isLoading = false;
    loader.hide();
  },
  methods: {
    omit,
    ...mapActions([
      'getHotelFacilitiesList',
      'getAllHotels',
      'getAllRooms',
    ]),
    clearSearchFilters(filters) {
      const newQuery = {
        ...this.$route.query,
      };
      filters.forEach(filter => {
        newQuery[filter] = undefined;
      });
      this.$router.replace({
        name: this.$route.name,
        query: newQuery,
      });
    },
    transformToRoomsList(roomTypes) {
      return Object.keys(roomTypes)
        .filter(key => roomTypes[key].mainImg)
        .map(key => ({
          id: key,
          title: roomTypes[key].rooms[0].roomName,
          price: Math.min(...roomTypes[key].rooms.map(({ price }) => price)),
          refundability: roomTypes[key].rooms.map(({ refundability }) => refundability).includes('RF') ? 'RF' : 'NRF',
          facilities: roomTypes[key].topAmenities,
          mainImg: roomTypes[key].mainImg || null,
          images: (roomTypes[key].images &&
            roomTypes[key].images.length)
            ? roomTypes[key].images.map(item => ({
              imageUrl: item,
              description: 'Room Photo',
            }))
            : [],
        }))
        .filter(({ title }) => {
          const targets = ['Deluxe', 'Suites'];
          const parts = title.toLowerCase().split(' ');
          return targets.some(target => parts.includes(target.toLowerCase()));
        })
        .sort((a, b) => {
          if (a.price < b.price) return -1;
          if (a.price > b.price) return 1;
          return 0;
        });
    },
    async submit(request) {
      const loader = this.$loading.show();
      const minWait = 300;
      const wait = Math.floor(Math.random() * Math.floor(7)) * 100;
      await new Promise(resolve => setTimeout(resolve, wait > 100 ? wait : minWait));

      if (request.hotel) {
        this.$router.push({
          name: 'content',
          params: {
            id: request.hotel.id,
          },
        });
      } else {
        this.city = request.city || null;
        const newQuery = {};
        Object.keys(request).forEach(param => {
          newQuery[param] = (request[param] && request[param].id) || undefined;
        });

        this.$router.replace({
          name: this.$route.name,
          query: newQuery,
        });
      }
      loader.hide();
    },
  },
};
</script>

<style lang="scss">
.page-search-hotels {
  height: 100%;
  display: grid;
  grid-template-areas:
    'sidebar search-panel search-panel'
    'sidebar content content';
  grid-template-columns: 255px 1fr 1fr;
  grid-template-rows: min-content 100%;
  @media screen and (max-width: 1024px) {
    grid-template-columns: 190px 1fr 1fr;
  }

  &__sidebar {
    grid-area: sidebar;
    background-color: #32325a;
    padding: 20px 0;
  }

  &__search {
    grid-area: search-panel;
  }

  &__content {
    grid-area: content;
    background: #fff;
    padding: 30px;
  }
}

.vertical-separator {
  display: inline-block;
  width: 1px;
  height: 58px;
  margin: 0 8px;
  background-color: rgba(#6e7c91, .07);
}
</style>
